import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, Plus, Edit2, Trash2, Save, Upload, X,
  Search, Lock, Camera, Smartphone, Phone, MapPin, Calendar,
  DollarSign, Fingerprint, CheckCircle, AlertCircle, UserCheck, UserX
} from 'lucide-react';
import StaffCalendarSync from './StaffCalendarSync';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Rôles disponibles
const ROLES = {
  admin: { level: 100, label: 'Administrator', color: 'purple' },
  manager: { level: 90, label: 'Manager', color: 'blue' },
  supervisor: { level: 75, label: 'Supervisor', color: 'cyan' },
  line_crew: { level: 30, label: 'Line Crew', color: 'green' },
  dispatcher: { level: 25, label: 'Dispatcher', color: 'yellow' },
  accountant: { level: 20, label: 'Accountant', color: 'orange' },
  cashier: { level: 20, label: 'Cashier', color: 'orange' },
  basic_user: { level: 10, label: 'Basic User', color: 'gray' },
  customer: { level: 10, label: 'Customer', color: 'gray' },
  read_only: { level: 5, label: 'Read Only', color: 'gray' },
};

const StaffManagement = ({ currentUserRole, currentUserId, canManageEmployees, canDeleteEmployees, canManagePasswords }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [selectedEmployeeForPassword, setSelectedEmployeeForPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  const [employeeForm, setEmployeeForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'basic_user',
    phone: '',
    telegram_id: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    hourly_rate: 0,
    overtime_rate: 0,
    hire_date: '',
    rfid_card_id: '',
    pin_code: '',
    photo_url: '',
    is_active: true
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/auth/staff`);
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    setUploadingPhoto(true);

    try {
      const response = await axios.post(`${API_BASE}/upload/staff-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setEmployeeForm({ ...employeeForm, photo_url: response.data.photo_url });
      setSaveStatus({ type: 'success', message: 'Photo uploaded successfully!' });
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error uploading photo:', error);
      setSaveStatus({ type: 'error', message: 'Error uploading photo' });
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSaveEmployee = async () => {
    try {
      if (editingEmployee) {
        await axios.put(`${API_BASE}/auth/staff/${editingEmployee.id}`, employeeForm);
        setSaveStatus({ type: 'success', message: 'Employee updated successfully!' });
      } else {
        await axios.post(`${API_BASE}/auth/staff`, employeeForm);
        setSaveStatus({ type: 'success', message: 'Employee created successfully!' });
      }
      await fetchEmployees();
      setShowEmployeeModal(false);
      setEditingEmployee(null);
      resetEmployeeForm();
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error saving employee:', error);
      setSaveStatus({ type: 'error', message: error.response?.data?.detail || 'Error saving employee' });
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setSaveStatus({ type: 'error', message: 'Password must be at least 6 characters' });
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }
    
    try {
      await axios.post(`${API_BASE}/auth/staff/${selectedEmployeeForPassword.id}/change-password`, {
        new_password: newPassword
      });
      setSaveStatus({ type: 'success', message: `Password changed for ${selectedEmployeeForPassword.first_name} ${selectedEmployeeForPassword.last_name}` });
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedEmployeeForPassword(null);
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error changing password:', error);
      setSaveStatus({ type: 'error', message: error.response?.data?.detail || 'Error changing password' });
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (!canDeleteEmployees) {
      setSaveStatus({ type: 'error', message: 'You do not have permission to delete employees.' });
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this employee? This action cannot be undone.')) {
      try {
        await axios.delete(`${API_BASE}/auth/staff/${employeeId}`);
        await fetchEmployees();
        setSaveStatus({ type: 'success', message: 'Employee deleted successfully!' });
        setTimeout(() => setSaveStatus(null), 3000);
      } catch (error) {
        console.error('Error deleting employee:', error);
        setSaveStatus({ type: 'error', message: 'Error deleting employee' });
        setTimeout(() => setSaveStatus(null), 3000);
      }
    }
  };

  const editEmployee = (employee) => {
    if (!canManageEmployees) {
      setSaveStatus({ type: 'error', message: 'You do not have permission to edit employees.' });
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }
    
    setEditingEmployee(employee);
    setEmployeeForm({
      email: employee.email || '',
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      role: employee.role || 'basic_user',
      phone: employee.phone || '',
      telegram_id: employee.telegram_id || '',
      address: employee.address || '',
      city: employee.city || '',
      state: employee.state || '',
      zip_code: employee.zip_code || '',
      hourly_rate: employee.hourly_rate || 0,
      overtime_rate: employee.overtime_rate || 0,
      hire_date: employee.hire_date ? employee.hire_date.split('T')[0] : '',
      rfid_card_id: employee.rfid_card_id || '',
      pin_code: employee.pin_code || '',
      photo_url: employee.photo_url || '',
      is_active: employee.is_active !== false
    });
    setShowEmployeeModal(true);
  };

  const openPasswordModal = (employee) => {
    if (!canManagePasswords) {
      setSaveStatus({ type: 'error', message: 'Only administrators can change employee passwords.' });
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }
    setSelectedEmployeeForPassword(employee);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const resetEmployeeForm = () => {
    setEmployeeForm({
      email: '',
      first_name: '',
      last_name: '',
      role: 'basic_user',
      phone: '',
      telegram_id: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      hourly_rate: 0,
      overtime_rate: 0,
      hire_date: '',
      rfid_card_id: '',
      pin_code: '',
      photo_url: '',
      is_active: true
    });
  };

  // Filtrer les employés
  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = searchTerm === '' || 
      `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.rfid_card_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || emp.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && emp.is_active) ||
      (statusFilter === 'inactive' && !emp.is_active);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const PermissionBadge = ({ role }) => {
    const roleInfo = ROLES[role] || ROLES.basic_user;
    const color = roleInfo.color;
    return (
      <span className={`px-2 py-1 rounded text-xs bg-${color}-500/20 text-${color}-400`}>
        {roleInfo.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-12 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
        <p className="text-gray-400 mt-4">Loading staff...</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Staff Management</h3>
          <p className="text-gray-400 text-sm">Manage employees, roles, RFID cards, and access</p>
        </div>
        <div className="flex gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search by name, email, RFID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500 w-64"
            />
          </div>
          
          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm"
          >
            <option value="all">All Roles</option>
            {Object.entries(ROLES).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          
          {canManageEmployees && (
            <button
              onClick={() => {
                setEditingEmployee(null);
                resetEmployeeForm();
                setShowEmployeeModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg transition text-white"
            >
              <Plus className="w-4 h-4" />
              Add Employee
            </button>
          )}
        </div>
      </div>

      {/* Status message */}
      {saveStatus && (
        <div className={`mb-4 p-3 rounded-lg ${saveStatus.type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
          {saveStatus.type === 'success' ? <CheckCircle className="w-4 h-4 inline mr-2" /> : <AlertCircle className="w-4 h-4 inline mr-2" />}
          {saveStatus.message}
        </div>
      )}

      {/* Employees Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-gray-700">
            <tr className="text-left text-gray-400 text-sm">
              <th className="pb-3">Photo</th>
              <th className="pb-3">Name</th>
              <th className="pb-3">Contact</th>
              <th className="pb-3">Role</th>
              <th className="pb-3">RFID Card</th>
              <th className="pb-3">Rate</th>
              <th className="pb-3">Status</th>
              <th className="pb-3">Google Calendar</th>
              <th className="pb-3">Actions</th>
             </tr>
          </thead>
          <tbody>
            {filteredEmployees.map((employee) => (
              <tr key={employee.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                <td className="py-3">
                  {employee.photo_url ? (
                    <img 
                      src={`${API_BASE}${employee.photo_url}`} 
                      alt={employee.first_name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                      <Users className="w-5 h-5 text-gray-500" />
                    </div>
                  )}
                 </td>
                <td className="py-3">
                  <div className="text-white font-medium">
                    {employee.first_name} {employee.last_name}
                  </div>
                  <div className="text-xs text-gray-500">{employee.email}</div>
                 </td>
                <td className="py-3">
                  <div className="text-sm text-gray-300">{employee.phone || '-'}</div>
                  <div className="text-xs text-gray-500">
                    {employee.telegram_id ? `@${employee.telegram_id}` : 'No Telegram'}
                  </div>
                 </td>
                <td className="py-3">
                  <PermissionBadge role={employee.role} />
                 </td>
                <td className="py-3">
                  <code className="text-xs bg-gray-900 px-2 py-1 rounded">
                    {employee.rfid_card_id || 'Not assigned'}
                  </code>
                 </td>
                <td className="py-3 text-white text-sm">
                  ${employee.hourly_rate}/hr
                 </td>
                <td className="py-3">
                  <span className={`px-2 py-1 rounded text-xs ${
                    employee.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                  }`}>
                    {employee.is_active ? 'Active' : 'Inactive'}
                  </span>
                 </td>
                <td className="py-3">
                  <StaffCalendarSync 
                    staff={employee} 
                    canManage={canManageEmployees}
                    onUpdate={fetchEmployees}
                  />
                 </td>
                <td className="py-3">
                  <div className="flex gap-2">
                    {canManageEmployees && (
                      <button
                        onClick={() => editEmployee(employee)}
                        className="p-1 hover:bg-gray-700 rounded transition"
                        title="Edit employee"
                      >
                        <Edit2 className="w-4 h-4 text-blue-400" />
                      </button>
                    )}
                    {canManagePasswords && (
                      <button
                        onClick={() => openPasswordModal(employee)}
                        className="p-1 hover:bg-gray-700 rounded transition"
                        title="Change password"
                      >
                        <Lock className="w-4 h-4 text-yellow-400" />
                      </button>
                    )}
                    {canDeleteEmployees && employee.id !== currentUserId && (
                      <button
                        onClick={() => handleDeleteEmployee(employee.id)}
                        className="p-1 hover:bg-gray-700 rounded transition"
                        title="Delete employee"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    )}
                  </div>
                 </td>
               </tr>
            ))}
          </tbody>
         </table>
        {filteredEmployees.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No employees found matching your filters
          </div>
        )}
      </div>

      {/* Employee Modal */}
      {showEmployeeModal && canManageEmployees && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                {editingEmployee ? 'Edit Employee' : 'Add Employee'}
              </h3>
              <button onClick={() => setShowEmployeeModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Photo Upload */}
              <div className="flex items-center gap-4 p-4 bg-gray-900/50 rounded-lg">
                <div className="relative">
                  {employeeForm.photo_url ? (
                    <img 
                      src={`${API_BASE}${employeeForm.photo_url}`} 
                      alt="Preview"
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gray-700 flex items-center justify-center">
                      <Camera className="w-8 h-8 text-gray-500" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e.target.files[0])}
                      className="hidden"
                    />
                    <div className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition inline-flex">
                      {uploadingPhoto ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      <span className="text-sm">Upload Photo</span>
                    </div>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">JPG, PNG or GIF. Max 2MB.</p>
                </div>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={employeeForm.first_name}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, first_name: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Last Name *</label>
                  <input
                    type="text"
                    value={employeeForm.last_name}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, last_name: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Email *</label>
                  <input
                    type="email"
                    value={employeeForm.email}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={employeeForm.phone}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Telegram ID</label>
                  <input
                    type="text"
                    value={employeeForm.telegram_id}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, telegram_id: e.target.value })}
                    placeholder="@username"
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Role *</label>
                  <select
                    value={employeeForm.role}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, role: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  >
                    {Object.entries(ROLES).map(([key, { label, level }]) => (
                      <option key={key} value={key}>{label} (Level {level})</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Address */}
              <div className="border-t border-gray-700 pt-4">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Address
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <input
                      type="text"
                      placeholder="Street Address"
                      value={employeeForm.address}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, address: e.target.value })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="City"
                      value={employeeForm.city}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, city: e.target.value })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="State"
                      value={employeeForm.state}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, state: e.target.value })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="ZIP Code"
                      value={employeeForm.zip_code}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, zip_code: e.target.value })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Employment */}
              <div className="border-t border-gray-700 pt-4">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Employment
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Hire Date</label>
                    <input
                      type="date"
                      value={employeeForm.hire_date}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, hire_date: e.target.value })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Hourly Rate ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={employeeForm.hourly_rate}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, hourly_rate: parseFloat(e.target.value) })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Overtime Rate ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={employeeForm.overtime_rate}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, overtime_rate: parseFloat(e.target.value) })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Status</label>
                    <select
                      value={employeeForm.is_active ? 'active' : 'inactive'}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, is_active: e.target.value === 'active' })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Security & Access */}
              <div className="border-t border-gray-700 pt-4">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Fingerprint className="w-4 h-4" /> Security & Access
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1">
                      <Fingerprint className="w-3 h-3" /> RFID Card ID
                    </label>
                    <input
                      type="text"
                      value={employeeForm.rfid_card_id}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, rfid_card_id: e.target.value.toUpperCase() })}
                      placeholder="Scan or enter RFID"
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Used for fuel pumps and time clock</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">PIN Code</label>
                    <input
                      type="password"
                      value={employeeForm.pin_code}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, pin_code: e.target.value })}
                      placeholder="4-6 digit PIN"
                      maxLength={6}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Password for new employees */}
              {!editingEmployee && (
                <div className="border-t border-gray-700 pt-4">
                  <label className="block text-sm text-gray-400 mb-1">Initial Password</label>
                  <input
                    type="password"
                    value={employeeForm.password || ''}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, password: e.target.value })}
                    placeholder="Leave empty to send email invitation"
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">If left empty, employee will receive an email to set their password</p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveEmployee}
                className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white"
              >
                {editingEmployee ? 'Update Employee' : 'Create Employee'}
              </button>
              <button
                onClick={() => setShowEmployeeModal(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && selectedEmployeeForPassword && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Change Password</h3>
              <button onClick={() => setShowPasswordModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-gray-400 mb-4">
              For <strong>{selectedEmployeeForPassword.first_name} {selectedEmployeeForPassword.last_name}</strong>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  placeholder="Minimum 6 characters"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleChangePassword}
                className="flex-1 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 rounded-lg text-white"
              >
                Change Password
              </button>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffManagement;